import { useEffect, useRef } from 'react';
import { useAdminNotificationStore } from '../stores/adminNotificationStore';
import { getAdminToken } from '../services/adminApi';
import { API_BASE_URL } from '../config/api';

export const useAdminSSE = () => {
  const { addNotification, setSseConnected } = useAdminNotificationStore();
  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const connect = async () => {
      const token = getAdminToken();
      if (!token) return;

      // Abort previous connection
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await fetch(`${API_BASE_URL}/admin/events`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error('SSE connection failed');
        }

        setSseConnected(true);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[ping]') continue;
              try {
                const event = JSON.parse(data);
                handleSSEEvent(event);
              } catch {
                // Ignore malformed JSON
              }
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setSseConnected(false);
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        }
      }
    };

    const handleSSEEvent = (event: Record<string, unknown>) => {
      const type = event.type as string;
      switch (type) {
        case 'handoff:requested':
          addNotification({
            type: 'handoff_requested',
            title: 'Customer Needs Help',
            message: (event.reason as string) || 'A customer has been escalated to human support.',
            urgency: (event.urgencyLevel as 'low' | 'medium' | 'high' | 'critical') || 'medium',
            workspaceId: event.workspaceId as string | undefined,
            conversationId: event.conversationId as string | undefined,
            timestamp: new Date(),
          });
          break;
        case 'notification:merchant':
          addNotification({
            type: 'merchant_notification',
            title: 'Merchant Notification',
            message: (event.message as string) || 'New merchant notification.',
            urgency: 'medium',
            workspaceId: event.workspaceId as string | undefined,
            timestamp: new Date(),
          });
          break;
        case 'handoff:resolved':
          addNotification({
            type: 'handoff_resolved',
            title: 'Escalation Resolved',
            message: 'Conversation has been resolved.',
            urgency: 'low',
            conversationId: event.conversationId as string | undefined,
            timestamp: new Date(),
          });
          break;
      }
    };

    connect();

    return () => {
      abortControllerRef.current?.abort();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      setSseConnected(false);
    };
  }, [addNotification, setSseConnected]);
};
